import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, Outlet } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCloudUploadAlt, faSignOutAlt, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../Config.js';
import { useLogout } from '../Login/LogoutPage.jsx';
import SearchModal from '../Inventory/SearchModal.js';
import Sidebar from './Sidebar.js';
import styles from './AppLayout.module.css';

const CASH_COLLECTION_REFRESH_MS = 15000;

// Dashboard shell: top bar + sidebar menu, with the current route rendered in <Outlet />
const AppLayout = ({ userData }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [todayCashCollection, setTodayCashCollection] = useState(null);
  const [storeName, setStoreName] = useState('');

  const handleLogout = useLogout();

  const fetchTodayCashCollection = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/getTodayUserCashCollection`, {
        params: { userId: userData.userId },
      });
      setTodayCashCollection(response.data);
    } catch (error) {
      console.error("Error fetching today's cash collection:", error);
      setTodayCashCollection(null);
    }
  }, [userData.userId]);

  useEffect(() => {
    fetchTodayCashCollection();
    const intervalId = setInterval(fetchTodayCashCollection, CASH_COLLECTION_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [fetchTodayCashCollection]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/store/getStoreName`, { params: { storeId: userData.storeId } })
      .then((response) => setStoreName(response.data))
      .catch((error) => console.error('Error fetching store name:', error));
  }, [userData.storeId]);

  // Ctrl+F opens the item search
  useEffect(() => {
    const handleShortcutKey = (event) => {
      if (event.ctrlKey && event.key === 'f') {
        event.preventDefault();
        setIsSearchModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleShortcutKey);
    return () => {
      window.removeEventListener('keydown', handleShortcutKey);
    };
  }, []);

  const handleBackup = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/backup`);
      alert(response.data);
    } catch (error) {
      console.error('Error creating backup:', error);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedRight: () => setIsSidebarOpen(true),
    onSwipedLeft: () => setIsSidebarOpen(false),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <div className={styles.shell} {...swipeHandlers}>
      <header className={styles.topBar}>
        <h1 className={styles.brand}>
          <Link to="/" className={styles.brandLink} onClick={() => setIsSidebarOpen(true)}>
            {storeName}
          </Link>
        </h1>

        <div className={styles.userInfo}>
          <span>User: {userData.sname}</span>
          <span>Shop: {userData.storeId}</span>
          <span className={styles.cashCollection}>
            <span>Cash Collection: {todayCashCollection}</span>
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={fetchTodayCashCollection}
              aria-label="Refresh cash collection"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
            </button>
          </span>

          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>

          <button type="button" className={styles.backupBtn} onClick={handleBackup}>
            <FontAwesomeIcon icon={faCloudUploadAlt} className={styles.backupIcon} /> Backup
          </button>
        </div>

        <button
          type="button"
          className={styles.menuToggle}
          onClick={() => setIsSidebarOpen((open) => !open)}
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isSidebarOpen}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      </header>

      <Sidebar isOpen={isSidebarOpen} onNavigate={closeSidebar} />
      {isSidebarOpen && <div className={styles.backdrop} onClick={closeSidebar} aria-hidden="true" />}

      <main className={`${styles.main} ${isSidebarOpen ? styles.mainShifted : ''}`}>
        <Outlet />
      </main>

      <SearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} />
    </div>
  );
};

export default AppLayout;
